<template>
  <form class="form-body" @submit.prevent="sendCompanyOnboardingInfoIfFormIsFilled">
    <Text variant="h1">Tailor Tallo to your needs</Text>
    <Text variant="body-large" color="secondary" class="sub-header">
      Providing this information allow us to unlock customized AI solutions that streamline your leasing process and
      maximize efficiency.
    </Text>

    <FormGroup>
      <FormField label="Phone number" :error="form.fieldErrors('phoneNumber')">
        <InputPhone v-model="form.fields.phoneNumber" />
      </FormField>

      <FormField label="What is your company name?">
        <Input v-model="form.fields.companyName" placeholder="Type the company name" />
      </FormField>

      <FormField label="How large is your company?">
        <Select v-model="form.fields.numberOfEmployees">
          <option disabled :value="null">Select company size</option>
          <option v-for="option in companyNumberOfEmployeesOptions" :key="option" :value="option">{{ option }}</option>
        </Select>
      </FormField>

      <FormField label="How many units do you oversee?" :error="form.fieldErrors('numberOfUnitsOverseeing')">
        <Input type="number" v-model="form.fields.numberOfUnitsOverseeing" placeholder="Type number of units" />
      </FormField>

      <FormField label="What would you like to see Tallo help you with?">
        <Textarea maxlength="200" v-model="form.fields.perceivedProductValuePropostion" />
      </FormField>
    </FormGroup>

    <View direction="column" gap="sm">
      <Button type="submit" :loading="form.submitting">Continue</Button>
      <Button type="button" @click="openNewPropertyOnboarding" :variant="'ghost'">I'll do it later</Button>
    </View>
  </form>
</template>

<script setup lang="ts">
import { minValue } from '@vuelidate/validators';
import { useRoute } from 'vue-router';

import {
  phoneNumberValidator,
  Button,
  FormField,
  FormGroup,
  Input,
  InputPhone,
  Select,
  Textarea,
  Text,
  View,
  useForm,
} from '@tallo/design-system';
import { CompanyNumberOfEmployees, companyNumberOfEmployeesOptions, companyService } from '@tallo/investor/company';
import { useNavigation } from '@tallo/investor/navigation';

const { openNewPropertyOnboarding } = useNavigation();
const route = useRoute();

const form = useForm<{
  phoneNumber: string;
  companyName: string;
  numberOfEmployees: CompanyNumberOfEmployees | null;
  numberOfUnitsOverseeing: number | null;
  perceivedProductValuePropostion: string;
}>(
  {
    phoneNumber: '',
    companyName: '',
    numberOfEmployees: null,
    numberOfUnitsOverseeing: null,
    perceivedProductValuePropostion: '',
  },
  {
    phoneNumber: {
      phoneNumberValidator,
    },
    numberOfUnitsOverseeing: {
      minValue: minValue(1),
    },
  },
);

const sendCompanyOnboardingInfoIfFormIsFilled = async () => {
  if (
    form.fields.companyName ||
    form.fields.numberOfEmployees ||
    form.fields.perceivedProductValuePropostion ||
    form.fields.phoneNumber ||
    form.fields.numberOfUnitsOverseeing
  ) {
    await sendCompanyOnboardingInfo();
  } else {
    openNewPropertyOnboarding();
  }
};

const sendCompanyOnboardingInfo = async () => {
  const companyId = route.query.companyId as string;

  await form.submit(async () => {
    await companyService.sendCompanyOnboardingInfo(
      {
        ...(form.fields.phoneNumber && { phoneNumber: form.fields.phoneNumber }),
        ...(form.fields.companyName && { companyName: form.fields.companyName }),
        ...(form.fields.numberOfEmployees && { numberOfEmployees: form.fields.numberOfEmployees }),
        ...(form.fields.numberOfUnitsOverseeing && { numberOfUnitsOverseeing: form.fields.numberOfUnitsOverseeing }),
        ...(form.fields.perceivedProductValuePropostion && {
          perceivedProductValuePropostion: form.fields.perceivedProductValuePropostion,
        }),
      },
      companyId,
    );
  });

  openNewPropertyOnboarding();
};
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.form-body {
  padding: 6.625rem 4rem 6rem;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 40rem;
  margin: var(--t-spacing-lg) auto;
  border-radius: var(--t-border-radius-md);
  min-width: 25rem;
  gap: var(--t-spacing-lg);
  background: var(--t-color-neutral-white);

  @include mobile {
    padding: 2.5rem 1.5rem;
    min-width: 0;
    border-radius: var(--t-border-radius-none);
    max-width: none;
    margin: 0;
    height: 100%;
  }

  @include tablet-start {
    padding: 4rem 4rem 6rem;
  }
}
</style>
